/**
 * Seed data for Camp Claim Feature Development
 * 
 * This file contains dummy camp data specifically for testing the camp claiming flow.
 * These camps include the new claim-related fields that will be added to the database schema.
 * 
 * DO NOT use this for production - it's for UI development only.
 */

import type { Camp } from '../types/database';

export interface ClaimableCamp extends Camp {
  claimed: boolean;
  owner_id: string | null;
  plan_id: string | null;
  claimed_at: string | null;
}

export const CLAIM_SEED_CAMPS: ClaimableCamp[] = [
  {
    id: 'seed-1',
    name: 'Adventure Kids Summer Camp',
    description: 'An exciting summer camp with outdoor adventures, team building, and creative activities for kids ages 6-12.',
    address: '123 Camp Road',
    city: 'Austin',
    state: 'Texas',
    zip_code: '78701',
    latitude: 30.2672,
    longitude: -97.7431,
    website_url: 'https://example.com/adventure-kids',
    phone: '(512) 555-0100',
    email: 'info@adventurekids.com',
    min_age: 6,
    max_age: 12,
    category_id: null,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    slug: 'texas/austin/adventure-kids-summer-camp',
    short_description: 'Outdoor adventures and creative activities',
    start_hour_1: '09:00',
    finish_hour_1: '15:00',
    start_hour_2: null,
    finish_hour_2: null,
    extended_time_available: true,
    lunch_provided: true,
    five_day_cost: 350,
    lunch_fee: 25,
    dates_available: ['June 5-9', 'June 12-16', 'June 19-23'],
    notes: 'Sunscreen and water bottle required daily',
    active: true,
    score: 0,
    ai_enriched: null,
    // Claim-related fields (will be added in Task 3)
    claimed: false,
    owner_id: null,
    plan_id: null,
    claimed_at: null,
  },
  {
    id: 'seed-2',
    name: 'Tech Explorers Coding Camp',
    description: 'Learn coding, robotics, and game design in a fun, interactive environment. Perfect for kids who love technology!',
    address: '456 Innovation Ave',
    city: 'Austin',
    state: 'Texas',
    zip_code: '78702',
    latitude: 30.2711,
    longitude: -97.7437,
    website_url: 'https://example.com/tech-explorers',
    phone: '(512) 555-0200',
    email: 'hello@techexplorers.com',
    min_age: 8,
    max_age: 14,
    category_id: null,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    slug: 'texas/austin/tech-explorers-coding-camp',
    short_description: 'Coding, robotics, and game design',
    start_hour_1: '09:00',
    finish_hour_1: '16:00',
    start_hour_2: null,
    finish_hour_2: null,
    extended_time_available: false,
    lunch_provided: true,
    five_day_cost: 450,
    lunch_fee: null,
    dates_available: ['July 10-14', 'July 17-21', 'July 24-28'],
    notes: 'Laptops provided - no need to bring your own',
    active: true,
    score: 0,
    ai_enriched: null,
    // Claim-related fields - THIS CAMP IS CLAIMED
    claimed: true,
    owner_id: 'user-abc-123',
    plan_id: 'campfire',
    claimed_at: '2025-01-15T10:30:00Z',
  },
  {
    id: 'seed-3',
    name: 'Nature Discovery Camp',
    description: 'Explore nature through hiking, wildlife observation, and environmental science activities.',
    address: '789 Forest Lane',
    city: 'Austin',
    state: 'Texas',
    zip_code: '78703',
    latitude: 30.2812,
    longitude: -97.7431,
    website_url: 'https://example.com/nature-discovery',
    phone: '(512) 555-0300',
    email: 'contact@naturediscovery.com',
    min_age: 7,
    max_age: 13,
    category_id: null,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    slug: 'texas/austin/nature-discovery-camp',
    short_description: 'Hiking, wildlife, and environmental science',
    start_hour_1: '08:30',
    finish_hour_1: '14:30',
    start_hour_2: null,
    finish_hour_2: null,
    extended_time_available: true,
    lunch_provided: false,
    five_day_cost: 275,
    lunch_fee: null,
    dates_available: ['June 26-30', 'July 3-7'],
    notes: 'Please pack a lunch and wear comfortable shoes',
    active: true,
    score: 0,
    ai_enriched: null,
    // Claim-related fields
    claimed: false,
    owner_id: null,
    plan_id: null,
    claimed_at: null,
  },
];

/**
 * Pricing Plans for Camp Owners
 * These correspond to the plans in scripts/camphiz-plans.pdf
 */
export interface PricingPlan {
  id: string;
  name: string;
  tagline: string;
  description: string; // Detailed description for info popover
  price: number;
  annualPrice: number;
  annualOriginalPrice?: number;
  earlyBirdPrice: number; // 50% off promotion
  monthlyPrice?: number;
  monthlyOriginalPrice?: number;
  isPopular: boolean;
  isFree?: boolean;
  features: string[]; // Features with asterisk (*) are coming soon
  includesPreviousPlan?: string; // Name of previous plan to reference
  color: string; // For UI highlighting
}

export const OWNER_PRICING_PLANS: PricingPlan[] = [
  {
    id: 'campsite',
    name: 'Campsite',
    tagline: 'Claim your spot on the map.',
    description: 'Your camp appears in CampWhiz search and can be claimed by your team. Perfect for getting discovered, but you can\'t enhance your listing with custom content and images.',
    price: 0,
    annualPrice: 0,
    earlyBirdPrice: 0,
    isPopular: false,
    isFree: true,
    monthlyPrice: 0,
    monthlyOriginalPrice: 0,
    annualOriginalPrice: 0,
    features: [
      'Get listed in CampWhiz search',
      'Claim your camp profile',
    ],
    color: 'gray',
  },
  {
    id: 'trailhead',
    name: 'Trailhead',
    tagline: 'Control & customize.',
    description: 'Unlock editing tools to update your camp\'s info, photos, and hours. Control how families see you.',
    price: 290,
    annualPrice: 149,
    annualOriginalPrice: 290,
    earlyBirdPrice: 49.50,
    monthlyPrice: 15,
    monthlyOriginalPrice: 29,
    stripeMonthlyPriceId: '',
    stripeAnnualPriceId: '',
    isPopular: false,
    includesPreviousPlan: 'Campsite',
    features: [
      'Edit your listing (description, hours, photos)',
    ],
    color: 'gray',
  },
  {
    id: 'campfire',
    name: 'Campfire',
    tagline: 'Boost your visibility.',
    description: 'This plan gets your camp higher placement in search results so families see you first. You also get a "Featured Camp" badge on your profile and stand out from the crowd.',
    price: 490,
    annualPrice: 249,
    annualOriginalPrice: 490,
    earlyBirdPrice: 149.50,
    monthlyPrice: 25,
    monthlyOriginalPrice: 49,
    stripeMonthlyPriceId: '',
    stripeAnnualPriceId: '',
    isPopular: true, // Most popular!
    includesPreviousPlan: 'Trailhead',
    features: [
      'Higher placement in local results',
      'Featured Camp badge on profile',
    ],
    color: 'orange',
  },
  {
    id: 'summit',
    name: 'Summit',
    tagline: 'We handle all the details.',
    description: 'Get concierge-level support — we maintain your listing, update content, and keep everything current for you.',
    price: 790,
    annualPrice: 399,
    annualOriginalPrice: 790,
    earlyBirdPrice: 249.50,
    monthlyPrice: 40,
    monthlyOriginalPrice: 79,
    stripeMonthlyPriceId: '',
    stripeAnnualPriceId: '',
    isPopular: false,
    includesPreviousPlan: 'Campfire',
    features: [
      'Dedicated onboarding and support',
      'White-glove profile management (we update and maintain for you)',
    ],
    color: 'blue',
  },
];

/**
 * User Types for Registration
 */
export type UserType = 'parent' | 'camp-owner';

export interface UserTypeOption {
  value: UserType;
  label: string;
  description: string;
  icon: string; // Heroicon name
}

export const USER_TYPE_OPTIONS: UserTypeOption[] = [
  {
    value: 'parent',
    label: 'Parent',
    description: 'Looking for camps for my children',
    icon: 'UserGroupIcon',
  },
  {
    value: 'camp-owner',
    label: 'Camp Owner',
    description: 'I own or manage a summer camp',
    icon: 'BuildingOfficeIcon',
  },
];

